import React from 'react';
import { useQuery } from 'react-query';
import { adminAPI } from '../../services/api';
import { 
  Database, 
  Server, 
  Activity, 
  Users,
  Package,
  ShoppingCart,
  DollarSign,
  Settings
} from 'lucide-react';
import LoadingSpinner from '../LoadingSpinner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const Statistics = () => {
  const { data: stats, isLoading } = useQuery(
    'admin-statistics',
    adminAPI.getStatistics,
    {
      select: (response) => response.data.data,
      refetchInterval: 30000 // Refresh every 30 seconds
    }
  );

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatUptime = (seconds) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    
    return parts.join(' ') || '< 1m';
  };

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const dbStats = stats?.database || {};
  const orderStats = stats?.orders || [];
  const systemInfo = stats?.system || {};
  const recentActivity = stats?.recentActivity || [];

  // Prepare chart data
  const orderChartData = orderStats.map(stat => ({
    status: stat.status.charAt(0).toUpperCase() + stat.status.slice(1),
    count: stat.count,
    revenue: stat.revenue
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-medium text-gray-900">System Statistics</h2>
        <p className="mt-1 text-sm text-gray-500">
          Detailed system metrics and performance data
        </p>
      </div>

      {/* Database Statistics */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center">
            <Database className="h-5 w-5 mr-2" />
            Database Statistics
          </h3>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{dbStats.products || 0}</div>
              <div className="text-sm text-gray-500">Products</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{dbStats.variants || 0}</div>
              <div className="text-sm text-gray-500">Variants</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{dbStats.orders || 0}</div>
              <div className="text-sm text-gray-500">Orders</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">{dbStats.apiConfigs || 0}</div>
              <div className="text-sm text-gray-500">API Configs</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{dbStats.users || 0}</div>
              <div className="text-sm text-gray-500">Users</div>
            </div>
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title flex items-center">
            <Server className="h-5 w-5 mr-2" />
            System Information
          </h3>
        </div>
        <div className="card-content">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <div className="text-sm text-gray-500">Node.js Version</div>
              <div className="text-lg font-medium text-gray-900">{systemInfo.nodeVersion}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Platform</div>
              <div className="text-lg font-medium text-gray-900 capitalize">{systemInfo.platform}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Uptime</div>
              <div className="text-lg font-medium text-gray-900">
                {formatUptime(systemInfo.uptime || 0)}
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Environment</div>
              <div className="text-lg font-medium text-gray-900 capitalize">{systemInfo.environment}</div>
            </div>
          </div>

          {systemInfo.memoryUsage && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Memory Usage</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <div className="text-sm text-gray-500">Heap Used</div>
                  <div className="text-lg font-medium text-gray-900">
                    {formatBytes(systemInfo.memoryUsage.heapUsed)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">Heap Total</div>
                  <div className="text-lg font-medium text-gray-900">
                    {formatBytes(systemInfo.memoryUsage.heapTotal)}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-500">RSS</div>
                  <div className="text-lg font-medium text-gray-900">
                    {formatBytes(systemInfo.memoryUsage.rss)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order Statistics Charts */}
      {orderStats.length > 0 && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Order Count by Status */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Orders by Status</h3>
            </div>
            <div className="card-content">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={orderChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="status" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Revenue by Status */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Revenue by Status</h3>
            </div>
            <div className="card-content">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={orderChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ status, revenue }) => `${status}: $${revenue.toFixed(2)}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="revenue"
                  >
                    {orderChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title flex items-center">
              <Activity className="h-5 w-5 mr-2" />
              Recent Activity (Last 24 Hours)
            </h3>
          </div>
          <div className="card-content">
            <div className="flow-root">
              <ul className="-mb-8">
                {recentActivity.slice(0, 10).map((activity, index) => (
                  <li key={index}>
                    <div className="relative pb-8">
                      {index !== recentActivity.length - 1 && index !== 9 && (
                        <span className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200" />
                      )}
                      <div className="relative flex space-x-3">
                        <div>
                          <span className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center ring-8 ring-white">
                            <ShoppingCart className="h-4 w-4 text-white" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                          <div>
                            <p className="text-sm text-gray-900">
                              {activity.type === 'order' ? 'Order' : 'Activity'} {activity.reference}
                            </p>
                            <p className="text-sm text-gray-500">Status: {activity.status}</p>
                          </div>
                          <div className="text-right text-sm whitespace-nowrap text-gray-500">
                            {new Date(activity.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <div className="card-content">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Package className="h-8 w-8 text-blue-500" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Total Products</div>
                <div className="text-2xl font-bold text-gray-900">{dbStats.products || 0}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ShoppingCart className="h-8 w-8 text-green-500" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Total Orders</div>
                <div className="text-2xl font-bold text-gray-900">{dbStats.orders || 0}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-purple-500" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">Active Users</div>
                <div className="text-2xl font-bold text-gray-900">{dbStats.users || 0}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-content">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Settings className="h-8 w-8 text-yellow-500" />
              </div>
              <div className="ml-4">
                <div className="text-sm font-medium text-gray-500">API Configs</div>
                <div className="text-2xl font-bold text-gray-900">{dbStats.apiConfigs || 0}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Statistics;
